import { useEffect, useMemo, useState } from "react";
import {
  dailyReportProblem, isEmail, parseRecipients,
} from "@components/scheduling/settings/settingsValidation";

const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

/**
 * The emailed end-of-day report: whether it goes out, to whom, and when.
 *
 * The recipients are one line on screen and a list in the draft. The line
 * is kept here as typed, so a trailing comma survives the keystroke that
 * added it; the draft gets the parsed list on every change, and the line is
 * re-read from the draft only when the draft stops agreeing with it, which
 * is what Discard and a reload look like from in here.
 */
export default function DailyReportEditor({
  enabled, recipients, time, onToggle, onRecipientsChange, onTimeChange,
}) {
  const list = useMemo(() => (Array.isArray(recipients) ? recipients : []), [recipients]);
  const [line, setLine] = useState(() => list.join(", "));
  useEffect(() => {
    setLine((current) => (
      JSON.stringify(parseRecipients(current)) === JSON.stringify(list) ? current : list.join(", ")
    ));
  }, [list]);

  const problem = dailyReportProblem({ enabled, recipients: list, time });
  const bad = list.filter((address) => !isEmail(address));

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          checked={Boolean(enabled)}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Email a report of the day's campaign calls
      </label>

      {enabled && (
        <>
          <div>
            <label className={labelClass} htmlFor="daily-report-recipients">Send to</label>
            <input id="daily-report-recipients" className={inputClass} value={line}
                   placeholder="frontdesk@clinic.example, manager@clinic.example"
                   onChange={(e) => {
                     setLine(e.target.value);
                     onRecipientsChange(parseRecipients(e.target.value));
                   }} />
            <p className="mt-1 text-xs text-gray-500">
              Separate addresses with commas. Each gets the summary and a spreadsheet of the
              day's calls.
            </p>
            {bad.length > 0 && (
              <p className="mt-1 text-xs text-red-600">
                Not an email address: {bad.join(", ")}.
              </p>
            )}
          </div>
          <div>
            <label className={labelClass} htmlFor="daily-report-time">Send at</label>
            <input id="daily-report-time" type="time" step={60}
                   className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   value={time ?? ""}
                   onChange={(e) => onTimeChange(e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">
              In the clinic's own time, after the last calling slot of the day.
            </p>
          </div>
          {problem && bad.length === 0 && (
            <p className="text-xs text-red-600">{problem}</p>
          )}
        </>
      )}
    </div>
  );
}
